import { useIsomorphicLayoutEffect } from '@react-hookz/web';
import { useBreakpointDown } from '@travel-frontend/core';
import { compute as computeScrollIntoViewActions } from 'compute-scroll-into-view'; // @TODO comes with downshift, do we need to add it explicitly still?
import { useCombobox } from 'downshift';
import { debounce, isEqual, sortBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as ActionTypes from '../../../../../components/SearchForm/components/DestinationField/actionTypes.js';
import { DESTINATION_TYPE_CITY, DESTINATION_TYPE_CONTINENT, DESTINATION_TYPE_COUNTRY, DESTINATION_TYPE_DESTINATION, DESTINATION_TYPE_HOTEL, DESTINATION_TYPE_MULTICOUNTRY, DESTINATION_TYPE_HOLIDAYHOME, PROMO_LINK_ITEM } from '../../../../../components/SearchForm/components/DestinationField/models.js';
import { useWidgetOverlay } from '../../../../../components/SearchForm/hooks/useWidgetOverlay.js';
const DISPLAY_PRIORITY = [DESTINATION_TYPE_MULTICOUNTRY, DESTINATION_TYPE_COUNTRY, DESTINATION_TYPE_DESTINATION, DESTINATION_TYPE_CITY, DESTINATION_TYPE_HOTEL, DESTINATION_TYPE_CONTINENT, DESTINATION_TYPE_HOLIDAYHOME];
export const useDestinationField = ({
  value: controlledValue,
  config: {
    required,
    templateItems,
    fetchDestinations: fetchDestinationsImplementation,
    widgetHead,
    airportsField,
    recentSearches,
    travelType
  },
  floatingOptions,
  invalid = false,
  disabled = false,
  dirty = false,
  layout = 'full',
  dimming = false,
  dispatch,
  onClickLastSearchesItem
}) => {
  const mdDown = useBreakpointDown('md');
  const widgetType = mdDown ? 'drawer' : 'popover';
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const handleOpenChange = open => {
    const currentInputValue = combobox.inputValue;
    const previousInputValue = destinationToString(controlledValue);
    if (open) {
      if (currentInputValue && destinations.length === 0) {
        setIsLoading(true);
        fetchDestinations(currentInputValue);
      }
    } else {
      if (currentInputValue !== previousInputValue) {
        combobox.setInputValue(previousInputValue);
      }
    }
    setWidgetOpen(open);
  };
  const filterItemsByTravelType = blocks => {
    return blocks.filter(block => {
      const blockTravelType = block.item?.travelType;
      if (!blockTravelType) {
        return true;
      }
      if (block.collection !== 'block_searchform_links') {
        return true;
      }
      return blockTravelType === travelType;
    });
  };
  const overlay = useWidgetOverlay({
    open: widgetOpen,
    type: widgetType,
    options: floatingOptions,
    dimming,
    onOpenChange: handleOpenChange,
    layout
  });
  const requestRef = useRef({
    promise: null,
    abortController: null
  });
  const cancelCurrentRequest = () => {
    requestRef.current.promise = null;
    if (requestRef.current.abortController != null) {
      requestRef.current.abortController.abort();
      requestRef.current.abortController = null;
    }
  };
  const clearSelection = useCallback(() => {
    cancelCurrentRequest();
    setDestinations([]);
    setIsLoading(false);
  }, []);
  const fetchDestinations = useCallback(async searchTerm => {
    cancelCurrentRequest();
    let promise;
    const abortController = new AbortController();
    requestRef.current.abortController = abortController;
    try {
      promise = fetchDestinationsImplementation({
        searchTerm,
        abortSignal: abortController.signal
      });
      requestRef.current.promise = promise;
      const destinations = await promise;
      setDestinations(
      // sorting must happen here because downshift needs to know before rendering
      sortBy(destinations, destination => DISPLAY_PRIORITY.indexOf(destination.type)));
    } catch {
      // ignore
    } finally {
      // last request?
      if (requestRef.current.promise === promise) {
        setIsLoading(false);
      }
    }
  }, [fetchDestinationsImplementation]);
  const handleInputValueChanged = useCallback(inputValue => {
    if (inputValue) {
      fetchDestinations(inputValue);
    } else {
      clearSelection();
    }
  }, [fetchDestinations, clearSelection]);
  const handleInputValueChangeDebounced = useMemo(() => debounce(handleInputValueChanged, 150), [handleInputValueChanged]);
  const handleSelectedItemChange = useCallback(({
    selectedItem
  }) => {
    if (selectedItem) {
      dispatch({
        type: ActionTypes.ValueChanged,
        payload: {
          value: selectedItem
        }
      });
      setWidgetOpen(false);
    }
  }, [dispatch]);
  const destinationToString = destination => destination?.keyword || '';
  const [focus, setFocus] = useState(false);
  const getInputProps = props => {
    return combobox.getInputProps({
      ...props,
      spellCheck: false,
      autoFocus: focus,
      onFocus: () => setFocus(true)
    }, {
      suppressRefError: true
    });
  };
  const getMenuProps = props => {
    return combobox.getMenuProps(props, {
      suppressRefError: true
    });
  };
  const [typing, isTyping] = useState(true);
  const combobox = useCombobox({
    items: destinations,
    selectedItem: controlledValue,
    onSelectedItemChange: handleSelectedItemChange,
    onInputValueChange: state => {
      const {
        inputValue = '',
        type
      } = state;
      // manual user input
      if (type === useCombobox.stateChangeTypes.InputChange) {
        isTyping(false);
        dispatch({
          type: ActionTypes.InputChanged,
          payload: {
            value: inputValue
          }
        });
        // make sure widget is open since it can be toggled
        if (!widgetOpen) {
          setWidgetOpen(true);
        }
      }
      if (type !== useCombobox.stateChangeTypes.InputChange) {
        isTyping(true);
      }
      // set to loading immediately to avoid flickering due to debouncing
      setIsLoading(true);
      // debounced to limit the number of requests
      handleInputValueChangeDebounced(inputValue);
    },
    isOpen: widgetOpen,
    itemToString: destinationToString,
    // By default donwshift assumes `menuNode` itself to be scrollable.
    // Since we add more stuff around the pure results though, we have another scroll boundary.
    // see: https://github.com/downshift-js/downshift/blob/3571528a751a7b6d82a01e00a2b28278876b5d16/src/utils.js#L20
    scrollIntoView: (node, menuNode) => {
      const previousNode = node.previousElementSibling;
      const nodeToCheck = previousNode != null && Object.hasOwn(previousNode.dataset, 'optionGroupLabel') ? previousNode : node;
      const actions = computeScrollIntoViewActions(nodeToCheck, {
        block: 'nearest',
        scrollMode: 'if-needed'
      });
      for (const {
        el,
        top,
        left
      } of actions) {
        el.scrollTop = top;
        el.scrollLeft = left;
      }
    }
  });
  const handleSelectTopDestination = useCallback(topDestination => {
    if (topDestination.id === PROMO_LINK_ITEM) {
      dispatch({
        type: ActionTypes.PromotionClicked,
        payload: {
          value: topDestination
        },
        meta: {
          isTopDestination: true
        }
      });
      return;
    }
    dispatch({
      type: ActionTypes.ValueChanged,
      payload: {
        value: topDestination
      },
      meta: {
        isTopDestination: true
      }
    });
    combobox.setInputValue(destinationToString(topDestination));
    setWidgetOpen(false);
  }, [dispatch]);
  const clearInputValue = event => {
    event?.preventDefault();
    dispatch({
      type: ActionTypes.ResetClicked
    });
    combobox.setInputValue('');
  };
  const getFieldProps = () => {
    if (widgetType === 'drawer') {
      return {
        ...overlay.getReferenceProps(),
        value: destinationToString(controlledValue),
        active: widgetOpen,
        invalid,
        disabled
      };
    }
    return getInputProps({
      ...overlay.getReferenceProps(),
      active: widgetOpen,
      invalid,
      disabled,
      clearable: true,
      onClear: clearInputValue
    });
  };
  const getWidgetProps = () => ({
    ...overlay.getWidgetProps(),
    airportsConfig: airportsField,
    type: widgetType,
    open: widgetOpen,
    searchTerm: combobox.inputValue,
    destinations,
    templateItems: filterItemsByTravelType(templateItems),
    hasNoResultMessage: Boolean(combobox.inputValue) && destinations.length === 0,
    invalid,
    isLoading,
    selectedIndex: destinations.findIndex(destination => destination.id === controlledValue?.id),
    onSelectTopDestination: handleSelectTopDestination,
    onClose: () => {
      dispatch({
        type: ActionTypes.CloseClicked
      });
      handleOpenChange(false);
    },
    onClear: clearInputValue,
    highlightedIndex: combobox.highlightedIndex,
    getInputProps,
    getMenuProps,
    getItemProps: combobox.getItemProps,
    widgetHead,
    layout,
    dimming,
    recentSearches,
    typing,
    onClickLastSearchesItem: item => {
      handleOpenChange(false);
      onClickLastSearchesItem(item);
    }
  });
  const areValuesEqual = (valueA, valueB) => {
    return isEqual(valueA, valueB);
  };
  const setValue = nextValue => {
    if (!areValuesEqual(nextValue, controlledValue)) {
      dispatch({
        type: ActionTypes.FunctionValueChanged,
        payload: {
          value: nextValue
        }
      });
      // Make sure downshift's combobox gets the state update from outside
      combobox.setInputValue(destinationToString(nextValue));
    }
  };
  const checkValidity = () => required === false || Boolean(combobox.inputValue && controlledValue);
  // silence downshift warnings related to a11y stuff...
  // (because we use portaling we can't really fix these)
  // see: https://github.com/downshift-js/downshift/pull/1053#issuecomment-642113835
  useIsomorphicLayoutEffect(() => {
    // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0.
    getInputProps();
    // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0.
    getMenuProps();
  }, []);
  // unmount cleanup
  useEffect(() => () => {
    cancelCurrentRequest();
    handleInputValueChangeDebounced.cancel();
  }, [handleInputValueChangeDebounced]);
  return {
    isActive: widgetOpen,
    isInvalid: invalid,
    isDisabled: disabled,
    getFieldProps,
    getWidgetProps,
    setValue,
    checkValidity
  };
};